import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { Badge } from "../../components/Badge";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { RolesListQuery } from "./queries";
import { organizationStore } from "../../store/organization";
import { useEffect, useRef, useState } from "react";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Input } from "../../components/inputs/Input";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Roles } from "./type";

const client = createApolloClient();
const { getState } = organizationStore;

const buildSearch = (search: string) => [
  {
    display_name: {
      _ilike: `%${search}%`,
    },
  },
];

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: Roles;
}) => JSX.Element;

const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: Roles }) => string
): CellRenderer => {
  return (params) => (
    <>
      {permission ? (
        <Link to={linkFunc(params)}>
          <Badge>{params.value}</Badge>
        </Link>
      ) : (
        <Badge>{params.value}</Badge>
      )}
    </>
  );
};

export const rolesListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const rawOrganizationId = getState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  let where: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId && organizationId !== 0) {
    where = {
      ...where,
      organization_id: {
        _in: [0, organizationId],
      },
    };
  }
  const { data } = await client.query({
    query: RolesListQuery,
    variables: {
      where,
    },
  });
  console.log(data);
  return data.roles;
};

export const RoleList = () => {
  const { editCreatePermission } =
    usePermissions("roles");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const users = useLoaderData() as Roles[];
  const [rowData, setRowData] = useState<Roles[]>([]);
  useEffect(() => {
    setRowData(users);
  }, [users]);
  const [isActiveToggle, setIsActiveToggle] = useState(true);
  useEffect(() => {
    const filteredItems = isActiveToggle
        ? users.filter((user) => user.is_active)
        : users;
    setRowData(filteredItems);
  }, [users, isActiveToggle]);
  const generateColDefs = (): ColDef<Roles>[] => [
    {
      headerName: "Role Name",
      valueGetter: (p) => p.data?.display_name,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/roles/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Is Active?",
      valueGetter: (p) => p.data?.is_active,
      valueFormatter: (p) => {
        return p.data?.is_active ? "Yes" : "No";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => (p.data?.is_active ? "Yes" : "No"),
    },
    {
      headerName: "Organization",
      valueGetter: (p) => p.data?.organization?.name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Description",
      valueGetter: (p) => p.data?.description,
      filter: "agTextColumnFilter",
      minWidth: 150,
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.user?.username,
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
  ];
  const colDefs: ColDef<Roles>[] = generateColDefs();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `Roles_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className=" grid gap-2 grid-cols-6">
          <Breadcrumbs />
          <div className="col-span-2 col-start-5 mt-2">
            <Input
              name="search"
              placeholder={`Search Roles`}
              onChange={(s) => {
                navigate(
                  s && typeof s === "string" && s.length > 0
                    ? `/roles/list?search=${s}`
                    : `/roles/list`
                );
                revalidator.revalidate();
              }}
            />
          </div>
          <div className="py-2 col-span-2 col-start-7">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/roles/create`)}>
                Create
              </PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          rowHeight={rowHeight}
          autoSizeStrategy={autoSizeStrategy}
          pagination={true}
          gridOptions={gridOptions}
          paginationPageSize={100}
          onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
    </div>
  );
};
